import { supabase } from '../supabaseClient';

// Current user state (stored in memory and localStorage for compatibility)
let currentUser = null;
let currentUserId = null;

export function initializeUser() {
  const username = localStorage.getItem('currentUser');
  if (username) {
    currentUser = username;
    // We'll fetch the user ID when needed
  }
  return currentUser;
}

export function getCurrentUser() {
  if (!currentUser) {
    currentUser = localStorage.getItem('currentUser');
  }
  return currentUser;
}

async function getCurrentUserId() {
  if (currentUserId) return currentUserId;

  const username = getCurrentUser();
  if (!username) return null;

  const { data, error } = await supabase
    .from('users')
    .select('id')
    .eq('username', username)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user ID:', error);
    throw new Error(`Failed to get user ID: ${error.message}`);
  }

  if (!data) {
    throw new Error('User not found in database');
  }

  currentUserId = data.id;
  return currentUserId;
}

// User Profile
export async function getUserProfile() {
  const username = getCurrentUser();
  if (!username) return null;

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('username', username)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
}

export async function updateUserProfile(updates) {
  const username = getCurrentUser();
  if (!username) {
    console.error('updateUserProfile: No user logged in');
    return { success: false, error: { message: 'No user logged in' } };
  }

  const updateData = {
    primary_goal: updates.primaryGoal,
    target_areas: updates.targetAreas,
    days_per_week: updates.daysPerWeek ? parseInt(updates.daysPerWeek) : null,
    time_per_session: updates.timePerSession,
    home_workout_time: updates.homeWorkoutTime,
    gym_workout_time: updates.gymWorkoutTime,
    gym_days: updates.gymDays,
    rest_days: updates.restDays,
    gym_access: updates.gymAccess,
    equipment: updates.equipment
  };

  console.log('Updating user profile for:', username, 'with data:', updateData);

  const { data, error } = await supabase
    .from('users')
    .update(updateData)
    .eq('username', username)
    .select();

  if (error) {
    console.error('Error updating user profile:', error);
    return { success: false, error };
  }

  console.log('Profile update successful, returned data:', data);
  return { success: true, data: data[0] };
}

export async function createUser(userData) {
  const { data, error } = await supabase
    .from('users')
    .insert([{
      username: userData.username,
      display_name: userData.displayName,
      age: userData.age,
      gender: userData.gender,
      height: userData.height,
      weight: userData.weight,
      fitness_level: userData.fitnessLevel,
      pullups: userData.pullups,
      pushups: userData.pushups,
      body_fat: userData.bodyFat,
      primary_goal: userData.primaryGoal,
      target_areas: userData.targetAreas,
      timeframe: userData.timeframe,
      days_per_week: userData.daysPerWeek,
      time_per_session: userData.timePerSession,
      home_workout_time: userData.homeWorkoutTime,
      gym_workout_time: userData.gymWorkoutTime,
      gym_days: userData.gymDays,
      gym_access: userData.gymAccess,
      equipment: userData.equipment
    }])
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error creating user:', error);
    return { success: false, error };
  }

  return { success: true, data };
}

export async function getAllUsers() {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }

  return data;
}

export async function setUserPasscode(username, passcode) {
  // Hash the passcode before storing (simple hash for now)
  const hashedPasscode = btoa(passcode); // Base64 encode for simple obfuscation

  const { error } = await supabase
    .from('users')
    .update({ passcode: hashedPasscode })
    .eq('username', username);

  if (error) {
    console.error('Error setting passcode:', error);
    return { success: false, error };
  }

  return { success: true };
}

export async function verifyUserPasscode(username, passcode) {
  const { data, error } = await supabase
    .from('users')
    .select('passcode')
    .eq('username', username)
    .maybeSingle();

  if (error) {
    console.error('Error verifying passcode:', error);
    return { success: false, error };
  }

  if (!data) {
    return { success: false, error: { message: 'User not found' } };
  }

  // If no passcode is set, allow access (for backwards compatibility)
  if (!data.passcode) {
    return { success: true, needsSetup: true };
  }

  const hashedPasscode = btoa(passcode);
  if (data.passcode === hashedPasscode) {
    return { success: true, needsSetup: false };
  }

  return { success: false, error: { message: 'Incorrect passcode' } };
}

export function switchUser(username) {
  currentUser = username;
  currentUserId = null; // Reset user ID cache
  localStorage.setItem('currentUser', username);
  window.location.reload();
}

export function logoutUser() {
  currentUser = null;
  currentUserId = null;
  localStorage.removeItem('currentUser');
  window.location.href = '/user-select';
}

// Workout Plans
export async function getUserWorkoutPlans() {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('workout_plans')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    console.error('Error fetching workout plans:', error);
    return null;
  }

  return data?.plan_data;
}

export async function saveUserWorkoutPlans(plans) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  // Check if plans already exist
  const { data: existing } = await supabase
    .from('workout_plans')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    // Update existing
    const { error } = await supabase
      .from('workout_plans')
      .update({ plan_data: plans, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) console.error('Error updating workout plans:', error);
  } else {
    // Insert new
    const { error } = await supabase
      .from('workout_plans')
      .insert([{ user_id: userId, plan_data: plans }]);

    if (error) console.error('Error inserting workout plans:', error);
  }
}

// Workout History
export async function getUserWorkoutHistory() {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('workout_history')
    .select('*')
    .eq('user_id', userId)
    .order('workout_date', { ascending: false });

  if (error) {
    console.error('Error fetching workout history:', error);
    return [];
  }

  return data;
}

export async function saveUserWorkoutHistory(history) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  // Delete existing history for this user
  await supabase
    .from('workout_history')
    .delete()
    .eq('user_id', userId);

  // Insert new history
  const records = history.map(item => ({
    user_id: userId,
    workout_date: item.date,
    exercises: item
  }));

  const { error } = await supabase
    .from('workout_history')
    .insert(records);

  if (error) console.error('Error saving workout history:', error);
}

// Detailed Workout History
export async function getUserDetailedWorkoutHistory() {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('detailed_workout_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching detailed workout history:', error);
    return [];
  }

  return data.map(row => row.workout_data);
}

export async function saveUserDetailedWorkoutHistory(history) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  // Delete existing history for this user
  await supabase
    .from('detailed_workout_history')
    .delete()
    .eq('user_id', userId);

  // Insert new history
  const records = history.map(item => ({
    user_id: userId,
    workout_data: item
  }));

  const { error } = await supabase
    .from('detailed_workout_history')
    .insert(records);

  if (error) console.error('Error saving detailed workout history:', error);
}

// Measurements
export async function getUserMeasurements() {
  const userId = await getCurrentUserId();
  if (!userId) return {};

  const { data, error } = await supabase
    .from('measurements')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') return {}; // No rows found
    console.error('Error fetching measurements:', error);
    return {};
  }

  return data?.measurement_data || {};
}

export async function saveUserMeasurements(measurements) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { data: existing } = await supabase
    .from('measurements')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    // Update existing
    const { error } = await supabase
      .from('measurements')
      .update({ measurement_data: measurements, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) console.error('Error updating measurements:', error);
  } else {
    // Insert new
    const { error } = await supabase
      .from('measurements')
      .insert([{ user_id: userId, measurement_data: measurements }]);

    if (error) console.error('Error inserting measurements:', error);
  }
}

// Measurement History
export async function getUserMeasurementHistory() {
  const userId = await getCurrentUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from('measurement_history')
    .select('*')
    .eq('user_id', userId)
    .order('recorded_at', { ascending: false });

  if (error) {
    console.error('Error fetching measurement history:', error);
    return [];
  }

  return data.map(row => row.measurement_data);
}

export async function saveUserMeasurementHistory(history) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  // Delete existing history for this user
  await supabase
    .from('measurement_history')
    .delete()
    .eq('user_id', userId);

  // Insert new history
  const records = history.map(item => ({
    user_id: userId,
    measurement_data: item,
    recorded_at: item.date || new Date().toISOString()
  }));

  const { error } = await supabase
    .from('measurement_history')
    .insert(records);

  if (error) console.error('Error saving measurement history:', error);
}

// User Settings
export async function getUserLastPlanUpdate() {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('user_settings')
    .select('last_plan_update')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching last plan update:', error);
    return null;
  }

  return data?.last_plan_update;
}

export async function saveUserLastPlanUpdate(date) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { data: existing } = await supabase
    .from('user_settings')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('user_settings')
      .update({ last_plan_update: date, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) console.error('Error updating last plan update:', error);
  } else {
    const { error } = await supabase
      .from('user_settings')
      .insert([{ user_id: userId, last_plan_update: date }]);

    if (error) console.error('Error inserting last plan update:', error);
  }
}

export async function getUserLastPlanSummary() {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('user_settings')
    .select('last_plan_summary')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching last plan summary:', error);
    return null;
  }

  return data?.last_plan_summary;
}

export async function saveUserLastPlanSummary(summary) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { data: existing } = await supabase
    .from('user_settings')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('user_settings')
      .update({ last_plan_summary: summary, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) console.error('Error updating last plan summary:', error);
  } else {
    const { error } = await supabase
      .from('user_settings')
      .insert([{ user_id: userId, last_plan_summary: summary }]);

    if (error) console.error('Error inserting last plan summary:', error);
  }
}

export async function getUserWeeklyStrategy() {
  const userId = await getCurrentUserId();
  if (!userId) return null;

  const { data, error } = await supabase
    .from('user_settings')
    .select('weekly_strategy')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching weekly strategy:', error);
    return null;
  }

  return data?.weekly_strategy;
}

export async function saveUserWeeklyStrategy(strategy) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { data: existing } = await supabase
    .from('user_settings')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('user_settings')
      .update({ weekly_strategy: strategy, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) console.error('Error updating weekly strategy:', error);
  } else {
    const { error } = await supabase
      .from('user_settings')
      .insert([{ user_id: userId, weekly_strategy: strategy }]);

    if (error) console.error('Error inserting weekly strategy:', error);
  }
}

// Combined settings saver (for onboarding)
export async function saveUserSettings(settings) {
  const userId = await getCurrentUserId();
  if (!userId) return;

  const { data: existing } = await supabase
    .from('user_settings')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  const settingsData = {
    ...(settings.lastPlanUpdate && { last_plan_update: settings.lastPlanUpdate }),
    ...(settings.lastPlanSummary && { last_plan_summary: settings.lastPlanSummary }),
    updated_at: new Date().toISOString()
  };

  if (existing) {
    const { error } = await supabase
      .from('user_settings')
      .update(settingsData)
      .eq('id', existing.id);

    if (error) console.error('Error updating user settings:', error);
  } else {
    const { error } = await supabase
      .from('user_settings')
      .insert([{ user_id: userId, ...settingsData }]);

    if (error) console.error('Error inserting user settings:', error);
  }
}

// Migration function to move localStorage data to Supabase
export async function migrateLocalStorageToSupabase(username) {
  console.log('Starting migration for user:', username);

  // Helper to get localStorage data
  const getLocal = (key) => {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  };

  try {
    // Get or create user
    let { data: user, error } = await supabase
      .from('users')
      .select('*')
      .eq('username', username)
      .maybeSingle();

    if (error && error.code === 'PGRST116') {
      // User doesn't exist, get profile from localStorage
      const usersStr = localStorage.getItem('appUsers');
      if (usersStr) {
        const users = JSON.parse(usersStr);
        const userProfile = users.find(u => u.username === username);
        if (userProfile) {
          const result = await createUser(userProfile);
          user = result.data;
        }
      }
    }

    if (!user) {
      console.error('Could not create or find user');
      return;
    }

    const userId = user.id;
    currentUserId = userId;

    // Migrate workout plans
    const workoutPlans = getLocal(`user_${username}_workoutPlans`);
    if (workoutPlans) {
      await supabase
        .from('workout_plans')
        .upsert([{ user_id: userId, plan_data: workoutPlans }]);
      console.log('Migrated workout plans');
    }

    // Migrate workout history
    const workoutHistory = getLocal(`user_${username}_workoutHistory`);
    if (workoutHistory && workoutHistory.length > 0) {
      await saveUserWorkoutHistory(workoutHistory);
      console.log('Migrated workout history');
    }

    // Migrate detailed workout history
    const detailedHistory = getLocal(`user_${username}_detailedWorkoutHistory`);
    if (detailedHistory && detailedHistory.length > 0) {
      await saveUserDetailedWorkoutHistory(detailedHistory);
      console.log('Migrated detailed workout history');
    }

    // Migrate measurements
    const measurements = getLocal(`user_${username}_measurements`);
    if (measurements) {
      await saveUserMeasurements(measurements);
      console.log('Migrated measurements');
    }

    // Migrate measurement history
    const measurementHistory = getLocal(`user_${username}_measurementHistory`);
    if (measurementHistory && measurementHistory.length > 0) {
      await saveUserMeasurementHistory(measurementHistory);
      console.log('Migrated measurement history');
    }

    // Migrate settings
    const lastPlanUpdate = localStorage.getItem(`user_${username}_lastPlanUpdate`);
    if (lastPlanUpdate) {
      await saveUserLastPlanUpdate(lastPlanUpdate);
      console.log('Migrated last plan update');
    }

    const lastPlanSummary = localStorage.getItem(`user_${username}_lastPlanSummary`);
    if (lastPlanSummary) {
      await saveUserLastPlanSummary(lastPlanSummary);
      console.log('Migrated last plan summary');
    }

    console.log('Migration completed successfully!');
    return true;
  } catch (error) {
    console.error('Migration error:', error);
    return false;
  }
}

// User Preferences (for PWA install prompt and welcome tour)
export async function getUserPreferences() {
  const username = getCurrentUser();
  if (!username) return { hideInstallPrompt: false, hideWelcomeTour: false };

  const { data, error } = await supabase
    .from('users')
    .select('hide_install_prompt, hide_welcome_tour')
    .eq('username', username)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user preferences:', error);
    return { hideInstallPrompt: false, hideWelcomeTour: false };
  }

  return {
    hideInstallPrompt: data?.hide_install_prompt || false,
    hideWelcomeTour: data?.hide_welcome_tour || false
  };
}

export async function setHideInstallPrompt(hide = true) {
  const username = getCurrentUser();
  if (!username) return { success: false };

  const { error } = await supabase
    .from('users')
    .update({ hide_install_prompt: hide })
    .eq('username', username);

  if (error) {
    console.error('Error updating hide_install_prompt:', error);
    return { success: false, error };
  }

  return { success: true };
}

export async function setHideWelcomeTour(hide = true) {
  const username = getCurrentUser();
  if (!username) return { success: false };

  const { error } = await supabase
    .from('users')
    .update({ hide_welcome_tour: hide })
    .eq('username', username);

  if (error) {
    console.error('Error updating hide_welcome_tour:', error);
    return { success: false, error };
  }

  return { success: true };
}
